/**
 * ChoiceOptionsCard · next_level / ask 共用卡片
 *
 * 三态渲染（v4.5）：
 *  · 未回答（latest）→ 全部 options 可点击
 *  · 流式中（disabled）→ 全部 options 灰显不可点
 *  · 已回答（历史卡）→ 只展示用户选过那条；其余折叠为「展开剩余 N 项」按钮
 *
 * 已选项识别：通过 answerText（消息后的第一条 user content）和 option.label 严格相等比较。
 * 找不到匹配（答案是用户手输 / 走输入框）就当所有 options 都未选，全部折叠。
 *
 * 点击行为：父级 onPick 把 option.label 当作 user_query 发回（与 bottomOptions 同款）。
 */

import { CheckOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import classNames from 'classnames'
import type {
  AskCard,
  NextLevelCard
} from '../../../../../../../types/ai-classify'
import LayerValuesMultiselectPicker from './LayerValuesMultiselectPicker'
import styles from './card.module.scss'

interface Props {
  card: NextLevelCard | AskCard
  /** 是否禁用点击 · 流式中应传 true */
  disabled?: boolean
  /**
   * 已被回答时这条卡片应该折叠未选项 · 仅显示已选那条
   * 如果传了，会从 card.options 中找 label === answerText 的项视为已选
   */
  answerText?: string | null
  /** 点击 option 触发 · 父级负责 sendUserMessage */
  onPick?: (label: string, optionId: string) => void
  /** 自由选择 picker 需要的 sessionId (走 fetchLayerValues / postLayerValuesSelect) */
  sessionId?: string
}

const ChoiceOptionsCard: React.FC<Props> = ({
  card,
  disabled,
  answerText,
  onPick,
  sessionId
}) => {
  // hooks 必须无条件先调 — React Hooks rule-of-order. picker 分支 early return 在后.
  const [showAll, setShowAll] = useState(false)

  // 自由选择 picker 分支 (2026-05-18 截图 9/10/11) — ask + picker.kind 时优先走多选 UI,
  // 不渲染默认 options 单选 list. 历史回放时 disabled, 组件内部展示「(历史会话)」.
  if (card.kind === 'ask' && card.picker?.kind === 'layer_values_multiselect') {
    if (!sessionId) {
      return (
        <div className={styles.card}>
          <div className={styles.question}>{card.question}</div>
          <div style={{ padding: '12px 16px', color: '#ef4444' }}>
            缺少 session_id，无法加载候选值
          </div>
        </div>
      )
    }
    return (
      <LayerValuesMultiselectPicker
        picker={card.picker}
        sessionId={sessionId}
        question={card.question}
        disabled={disabled || !!answerText}
      />
    )
  }

  const isAnswered = !!answerText
  const pickedOption = isAnswered
    ? card.options.find((o) => o.label === answerText)
    : undefined

  const handleClick = (label: string, id: string): void => {
    if (disabled) return
    if (onPick) onPick(label, id)
  }

  /**
   * 一行能装下 → 横排 inline；装不下 → 上下布局 vertical
   *
   * CSS 单靠 flex-wrap 没法在"label 太长就把 label/detail 折成两行"语义上做对
   * （flex 会优先让 label 内部 text wrap，不会触发 flex 项换行），用字符数
   * 启发式判定即可。阈值按 sidebar 默认 420px 宽下大致估的：13px 字号 + 中文
   * 字符 ~15px / 拉丁 ~7px · padding 24 + gap 8 = 32 已扣，剩 ~388px ≈ 25 个
   * 中文宽度 · combined > 24 时大概率装不下，触发竖排
   */
  const inlineLayout = (
    card as {
      options: { label: string; detail?: string }[]
    }
  ).options.every((o) => (o.label?.length ?? 0) + (o.detail?.length ?? 0) <= 24)

  // 决定渲染哪些 option
  let visibleOptions = card.options
  let hiddenCount = 0
  if (isAnswered && !showAll) {
    if (pickedOption) {
      visibleOptions = [pickedOption]
      hiddenCount = card.options.length - 1
    } else {
      // 用户选了 options 之外的（手输 / 命中其它路径），全部折叠
      visibleOptions = []
      hiddenCount = card.options.length
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.question}>{card.question}</div>
      {visibleOptions.length > 0 && (
        <ul className={styles.optionList}>
          {visibleOptions.map((o) => {
            const isPicked = isAnswered && o.label === answerText
            return (
              <li
                key={o.id}
                role={disabled ? undefined : 'button'}
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                aria-pressed={isPicked || undefined}
                className={classNames(styles.optionItem, {
                  [styles.optionItemBtn]: !disabled,
                  [styles.optionItemDisabled]: disabled,
                  [styles.optionItemPicked]: isPicked,
                  [styles.optionItemInline]: inlineLayout
                })}
                onClick={() => handleClick(o.label, o.id)}
                onKeyDown={(e) => {
                  if (disabled) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleClick(o.label, o.id)
                  }
                }}
              >
                {/* 上下布局 · label 行 (含 check icon) 在上，detail 在下
                    长文案 label 不再挤压右侧 detail · 内部多行换行也整齐 */}
                <div className={styles.optionItemHeader}>
                  {isPicked && (
                    <CheckOutlined
                      style={{ color: '#10b981', marginRight: 4 }}
                    />
                  )}
                  <span className={styles.optionLabel}>{o.label}</span>
                </div>
                {o.detail && (
                  <span className={styles.optionDetail}>{o.detail}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {isAnswered && hiddenCount > 0 && (
        <button
          type="button"
          className={styles.optionToggle}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? '收起'
            : pickedOption
              ? `展开剩余 ${hiddenCount} 项`
              : `展开全部 ${hiddenCount} 项`}
        </button>
      )}
    </div>
  )
}

export default ChoiceOptionsCard
